import { useState, useEffect, useRef } from "react";
import { DB } from "../utils/indexedDB.js";

/**
 * Fetch with cache and optional online fallback using IndexedDB
 * 
 * @param {string} cacheKey The key for caching data
 * @param {Function} fetcher The function to fetch data (e.g., API call)
 * @param {Object} options Options for flexibility
 * @param {boolean} options.useCache If true, check the cache first
 * @param {boolean} options.forceRefresh If true, always refresh the cache (ignores cached data)
 * @param {number} options.cacheExpiration Cache expiration time in milliseconds (default: null, no expiration)
 * @returns {Promise} The fetched or cached data
 */
async function fetchWithCache(
    cacheKey,
    fetcher,
    options = { useCache: true, forceRefresh: false, cacheExpiration: null }
) {
    const { useCache, forceRefresh, cacheExpiration } = options;

    try {
        const cachedEntry = await DB.getItem(cacheKey);

        // Handle offline + cached data scenario
        if (!navigator.onLine) {
            if (cachedEntry) {
                // Return the data part of the cached entry
                return cachedEntry.data !== undefined ? cachedEntry.data : cachedEntry;
            } else {
                throw new Error("No internet connection and no cached data available.");
            }
        }

        // Check if cache has expired
        let cacheExpired = false;
        if (cachedEntry && cachedEntry.timestamp && cacheExpiration) {
            const now = Date.now();
            const cacheAge = now - cachedEntry.timestamp;
            cacheExpired = cacheAge > cacheExpiration;
        }

        // If using cache and data exists, return cached data unless forceRefresh is true or cache expired
        if (useCache && cachedEntry && !forceRefresh && !cacheExpired) {
            // Return the data part of the cached entry (for backwards compatibility)
            return cachedEntry.data !== undefined ? cachedEntry.data : cachedEntry;
        }

        // Fetch fresh data using the provided fetcher function
        const data = (typeof fetcher === 'function') ? await fetcher() : await fetcher.current();

        // Cache the fetched data with timestamp
        const cacheData = {
            data: data,
            timestamp: Date.now()
        };
        await DB.setItem(cacheKey, cacheData);

        return data; // Return the fresh data
    } catch (error) {
        // If fetch fails but cached data exists, use it as a fallback
        const cachedEntry = await DB.getItem(cacheKey);
        if (cachedEntry) {
            // Return the data part of the cached entry
            return cachedEntry.data !== undefined ? cachedEntry.data : cachedEntry;
        } else {
            throw error; // Throw error if no cache and fetch fails
        }
    }
};

function useCachedData(
    cacheKey,
    fetcher,
    options = { useCache: true, forceRefresh: false, cacheExpiration: null }
) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Store fetcher and options in refs to stabilize them
    const fetcherRef = useRef(fetcher);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const result = await fetchWithCache(cacheKey, fetcherRef, options);
                setData(result);
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [cacheKey]);

    return { data, loading, error };
}

export default useCachedData;
